import { CorePermissions, PermissionExplorer } from "app/appPermissions";
import { CurrentKnowledgeCallouts, fetchKnowledgeCallout } from "app/features/callout/calloutSlice";
import { fetchKnowledgeQuickAccess, knowledgeQuickAccess, QuickAccessDto } from "app/features/menu/menuSlice";
import { store, useAppDispatch, useAppSelector } from "app/store";
import { CalloutDto, FeedType } from "generated/CoreApi_v1";
import useEvent from "hooks/useEvent";
import { TFunction } from "i18next";
import { useInsideLayout } from "layouts/common/inside/InsideLayoutContext";
import { LateralBlockOption, LateralMenu, LateralMenuBlock, LateralMenuItem } from "layouts/common/MenuCommon";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction, useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";

////////////////////////////////////////////////////////////////////////////////

const buildMenuPub = (pe: PermissionExplorer, t: TFunction, callouts: CalloutDto[]): LateralMenuBlock | null => {

    var hasModeration = callouts.find(p => p.id == FeedType[FeedType.ModeratePublications] || p.id == FeedType[FeedType.ModerateReports] || p.id == FeedType[FeedType.ModerateChannelSubscriptions]) != null;

    var items: LateralMenuItem[] = [
        {
            path: KnowledgePaths.FeedStartup,
            name: t("App.Menu.Knowledge.FeedMain"),
            matchCalloutId: [FeedType[FeedType.Startup]]
        },
        {
            path: KnowledgePaths.FeedContributions,
            name: t("App.Menu.Knowledge.FeedContributions"),
            menuMatch: [KnowledgePaths.FeedMyContributions, KnowledgePaths.FeedMyAnswers, KnowledgePaths.FeedMyDrafts],
            matchCalloutId: [FeedType[FeedType.MyContributions]]
        },
        {
            path: KnowledgePaths.FeedHistory,
            name: t("App.Menu.Knowledge.FeedHistory"),
            matchCalloutId: [FeedType[FeedType.History]]
        }
    ];

    if (hasModeration) {
        items.push({
            path: KnowledgePaths.FeedModeration,
            name: t("App.Menu.Knowledge.FeedModeration"),
            menuMatch: [KnowledgePaths.FeedModerationPublications, KnowledgePaths.FeedModerationReports, KnowledgePaths.FeedModerationSubscriptions],
            matchCalloutId: [FeedType[FeedType.ModeratePublications], FeedType[FeedType.ModerateReports], FeedType[FeedType.ModerateChannelSubscriptions]]
        });
    }

    return {
        header: t("App.Menu.Knowledge.MainFeedsGroup"),
        blocks: [{ items }]
    };
};

const buildMenuExpore = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {
    return {
        header: t("App.Menu.Knowledge.ExploreGroup"),
        blocks: [{
            items: [
                { path: KnowledgePaths.FeedShorts, name: t("App.Menu.Knowledge.FeedShorts") },
                { path: KnowledgePaths.FeedQuestionAndAnswers, name: t("App.Menu.Knowledge.FeedQuestionAndAnswers"), matchCalloutId: [FeedType[FeedType.QuestionRecent], FeedType[FeedType.QuestionToYou], FeedType[FeedType.QuestionFollowing]] },
                { path: KnowledgePaths.Subcriptions, name: t("App.Menu.Knowledge.Subscribe") },
            ]
        }]
    };
};

const buildMenuLists = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {
    return {
        header: t("App.Menu.Knowledge.ListGroup"),
        blocks: [{
            items: [
                { path: KnowledgePaths.FeedFavorites, name: t("App.Menu.Knowledge.FeedFavorites") },
                { path: KnowledgePaths.FeedSeeLater, name: t("App.Menu.Knowledge.FeedSeeLater") },
                { path: KnowledgePaths.FeedMyLists, name: t("App.Menu.Knowledge.UserLists") },
            ]
        }]
    };
};

const buildMenuManager = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {
    var items: LateralMenuItem[] = [];

    if (pe.hasOne([CorePermissions.KnowledgeEngagementManager,
    CorePermissions.KnowledgeChannelsAdmin,
    CorePermissions.KnowledgeChannelsManager,
    CorePermissions.KnowledgeManagesSomeChannel]))
        items.push({ path: KnowledgePaths.ManagementChannel, name: t("App.Menu.Knowledge.ManageChannel") });

    if (pe.hasOne([CorePermissions.KnowledgeChannelsAdmin, CorePermissions.KnowledgeManagesSomeChannel]))
        items.push({ path: KnowledgePaths.ManagementPublication, name: t("App.Menu.Knowledge.ManagePublication") });

    if (pe.hasOne([CorePermissions.KnowledgeChannelsAdmin, CorePermissions.KnowledgeEngagementManager, CorePermissions.KnowledgeManagesSomeChannel])) {
        items.push({ path: KnowledgePaths.ManageShortVideos, name: t("App.Menu.Knowledge.ManageShorts") });
    }

    if (pe.hasOne([CorePermissions.KnowledgeEngagementManager, CorePermissions.KnowledgeAdmin])) {
        items.push({ path: KnowledgePaths.Settings, name: t("App.Menu.Knowledge.ManageConfiguration"), menuMatch: [KnowledgePaths.Settings] });
    }

    return items.length == 0
        ? null
        : {
            header: t("App.Menu.Knowledge.ManageGroup"),
            blocks: [{ items }]
        };
};

const buildMenuDataset = (pe: PermissionExplorer, t: TFunction, navigate: NavigateFunction, quickAccess: QuickAccessDto, callouts: CalloutDto[]): LateralMenuBlock[] => {

    const initialItems = quickAccess.items.filter((_, index) => index < 8);
    const blocks: LateralBlockOption[] = [{ items: initialItems }];

    if (!quickAccess.loading && initialItems.length < quickAccess.items.length) {
        blocks.push({
            collapsable: {
                id: "quickAccessAll",
                initialCollapse: true,
                collapsedHeader: t("App.Menu.Knowledge.QuickAccessCollapseHeader"),
                contractedHeader: t("App.Menu.Knowledge.QuickAccessContractedHeader")
            },
            items: [...quickAccess.items].sort((a, b) => a.name.localeCompare(b.name))
        });
    }

    const quickAccesBlock: LateralMenuBlock = {
        header: t("App.Menu.Knowledge.QuickAccess"),
        onEditAction: () => navigate(KnowledgePaths.QuickAccessEdit),
        loading: quickAccess.loading,
        blocks
    };

    const modules: (LateralMenuBlock | null)[] = [
        buildMenuPub(pe, t, callouts),
        buildMenuExpore(pe, t),
        buildMenuLists(pe, t),
        buildMenuManager(pe, t),
        quickAccesBlock,

    ];
    return modules.filter(p => p != null) as LateralMenuBlock[];
};

////////////////////////////////////////////////////////////////////////////////

export const KnowledgeMenu: React.FC = () => {
    const layout = useInsideLayout();
    const divRef = useRef<HTMLDivElement | null>(null);
    const session = useSession();
    const { t } = useTranslation("shared");
    const dispatch = useAppDispatch();
    const quickAccess = useAppSelector(knowledgeQuickAccess);
    const callouts = useAppSelector(CurrentKnowledgeCallouts);
    const navigate = useNavigate();

    /**
     * Cria os Blocos de Menu que são estáticos.
     **/
    const dataset = useMemo(() => buildMenuDataset(
        session.permissionExplorer,
        t,
        navigate,
        quickAccess,
        callouts
    ), [session.user.permissions, quickAccess, callouts]);

    /**
     * Obtém as subscrições do usuário para criar o Menu Rápido (menu dinâmico).
     */
    useEffect(() => {
        dispatch(fetchKnowledgeQuickAccess({}));
    }, []);

    /**
     * Executa o Polling dos Callout's do Menu de Conhecimento
     **/
    useEffect(() => {
        store.dispatch(fetchKnowledgeCallout({}));
        const timer = setInterval(() => store.dispatch(fetchKnowledgeCallout({})), 60000);
        return () => clearInterval(timer);
    }, []);

    /**
     * Monitora o evento 'click' e 'força' o fechamento do menu ao clicar em uma das opções.
     * */
    useEvent(divRef.current, "click", (e) => {
        if (layout.menuOpened && e.target instanceof HTMLAnchorElement && e.target.tagName == "A") {
            layout.toogleMenu();
        }
    });

    return (
        <div ref={divRef} style={{ height: "inherit" }}>
            <LateralMenu dataset={dataset} callouts={callouts} />
        </div>
    );
};
